<script context="module">
  // loader -> component, so a page opened before renders at once
  const resolved = new Map()

  /** Fetches a page's code ahead of time (e.g. when the browser is idle), without showing it. Never rejects. */
  export function prefetchPage(load) {
    if (resolved.has(load)) return Promise.resolve()
    return load()
      .then((module) => {
        resolved.set(load, module.default)
      })
      .catch(() => {})
  }
</script>

<script>
  // A page whose code is loaded the first time it is opened. Shows a spinner if that takes a moment, and a way
  // out if it fails (offline, or a new version was deployed and this one's files are gone: reloading fixes both).
  import { onDestroy } from 'svelte'
  import { _ } from 'svelte-i18n'
  import Loader from './Loader.svelte'

  /** () => import('../pages/SomePage.svelte') */
  export let load
  /** The page's props. */
  export let props = {}

  const SPINNER_DELAY_MS = 200

  let component = resolved.get(load) ?? null
  let failed = false
  let showSpinner = false
  let spinnerTimer

  $: start(load)

  function start(loader) {
    clearTimeout(spinnerTimer)
    failed = false
    if (resolved.has(loader)) {
      component = resolved.get(loader)
      return
    }
    component = null
    showSpinner = false
    spinnerTimer = setTimeout(() => (showSpinner = true), SPINNER_DELAY_MS)
    loader()
      .then((module) => {
        resolved.set(loader, module.default)
        if (loader === load) component = module.default
      })
      .catch((error) => {
        console.warn('page could not be loaded', error)
        if (loader === load) failed = true
      })
      .finally(() => clearTimeout(spinnerTimer))
  }

  onDestroy(() => clearTimeout(spinnerTimer))
</script>

{#if component}
  <svelte:component this={component} {...props} />
{:else if failed}
  <div class="container py-5 text-center" role="alert">
    <p>{$_('LAZY_PAGE.LOAD_ERROR')}</p>
    <button class="btn btn-primary s-button-primary" on:click={() => window.location.reload()}
      >{$_('LAZY_PAGE.RELOAD')}</button
    >
  </div>
{:else if showSpinner}
  <Loader />
{/if}
